<script setup lang="ts">
import { onMounted } from 'vue'
import { NEmpty, NSpin } from 'naive-ui'
import QueueErrorState from './QueueErrorState.vue'
import RouteShell from './RouteShell.vue'
import UsageSpendSection from './UsageSpendSection.vue'
import UsageAvailableModelsTable from './UsageAvailableModelsTable.vue'
import { useUsageStore } from '../stores/usage'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin-only cost / token-usage dashboard. Reads from
// `Api::V1::UsageController#index`'s 8-week-default response — the
// current week is computed live from in-flight `IngestionRun` /
// `LintRun` / `SynthesisRun` rows so the "this week so far" callout
// reflects spend up to the minute; past weeks read from frozen
// `UsageReport` snapshots written by `WeeklyUsageReportJob`.
//
// Auth gating happens at the router level (see router/index.ts) — the
// SPA redirects non-admins back to /. This view trusts that gate.
//
// The weekly-spend dashboard and the available-models table are split
// into their own components (#1090, formerly inline here) — this view
// just owns the top-level loading/error/empty branching and delegates.

const store = useUsageStore()

useDocumentTitle('Usage')

onMounted(() => {
  if (!store.loaded) void store.fetch()
  // Independent fetch — its own ~24h-cached endpoint, so a
  // model-catalog hiccup never blanks the spend view and vice versa.
  if (!store.modelsLoaded) void store.fetchModels()
})
</script>

<template>
  <RouteShell>
    <section class="mx-auto flex max-w-5xl flex-col gap-5 pb-6">
      <header class="flex flex-col gap-1.5">
        <h1 class="page-title">Usage</h1>
        <p class="m-0 text-sm text-ink-muted">
          Per-week token spend across Ingest, Lint, Synthesis and CV runs. The current week updates as runs land;
          previous weeks freeze on Monday cron via
          <code class="font-mono text-ink-faint">WeeklyUsageReportJob</code>.
        </p>
      </header>

      <!--
        Branch precedence: in-flight → error → empty → data. Keying off
        `loading` (toggled in the store's try/finally) rather than
        `loaded` because `loaded` only flips true on the SUCCESS path —
        a failed fetch leaves `loaded` false AND `loadError` populated,
        which would render the spinner forever if we ordered on
        `!loaded`. `loading` is the in-flight flag that ALSO becomes
        false on the error path, so the error branch lights up
        correctly.
      -->
      <div v-if="store.loading" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <QueueErrorState v-else-if="store.loadError" :message="store.loadError" @retry="store.fetch()" />

      <NEmpty
        v-else-if="store.weeks.length === 0"
        description="No usage data yet — Ingest / Lint / Synthesis runs will populate this view."
        class="my-12"
      />

      <!-- Two cards side by side on wide screens, stacked below ~700px. -->
      <div v-else class="grid grid-cols-[repeat(auto-fit,minmax(min(340px,100%),1fr))] items-start gap-5">
        <UsageSpendSection :current-week="store.currentWeek" :past-weeks="store.pastWeeks" />
      </div>

      <UsageAvailableModelsTable
        :loading="store.modelsLoading"
        :error="store.modelsError"
        :models="store.models"
        :stale="store.modelsStale"
        @retry="store.fetchModels()"
      />
    </section>
  </RouteShell>
</template>
